import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { HistoryMoodBoard } from '../components/moodboard/HistoryMoodBoard';
import { ArrowLeft, Calendar, Loader } from 'lucide-react';
import api from '../services/api';
import { Meeting } from '../types';

export const MeetingHistoryView: React.FC = () => {
    const { meetingId } = useParams<{ meetingId: string }>();
    const [meeting, setMeeting] = useState<Meeting | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchMeeting = async () => {
            if (!meetingId) return;
            try {
                // Since we don't have a direct "get meeting by id" for public user easily,
                // we assume user is logged in (which they are) and use the meetings list or similar.
                // However, the cleanest way is if backend supports GET /meetings/{id}.
                // Checking admin.py shows GET /meetings list, but regular user might need access.
                // meetings.py shows GET /meetings (list).
                // Lets try to find it from the list or implement GET /meetings/{id} for non-admins if needed.
                // For now, assuming GET /meetings returns it in the list.
                const res = await api.get('/meetings/');
                const found = res.data.find((m: Meeting) => m.id === parseInt(meetingId));
                setMeeting(found);
            } catch (err) {
                console.error("Failed to fetch meeting details", err);
            } finally {
                setLoading(false);
            }
        };
        fetchMeeting();
    }, [meetingId]);

    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <Loader className="w-8 h-8 animate-spin text-purple-600" />
            </div>
        );
    }

    if (!meeting) {
        return (
            <div className="flex h-screen flex-col items-center justify-center gap-4">
                <h2 className="text-2xl font-bold text-red-600">Meeting Not Found</h2>
                <Link to="/" className="text-blue-600 hover:underline">Go Home</Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            {/* Header */}
            <header className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/" className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                            <ArrowLeft className="w-6 h-6 text-gray-600" />
                        </Link>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                {meeting.name}
                                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">History</span>
                            </h1>
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {meeting.frequency}
                            </p>
                        </div>
                    </div>
                </div>
            </header>

            {/* Content */}
            <main className="flex-1 p-6 overflow-hidden">
                <HistoryMoodBoard meetingId={meeting.id} />
            </main>
        </div>
    );
};
